import { Pipe, PipeTransform } from '@angular/core';
import { AppService } from 'src/app/app.service';

@Pipe({
  name: 'currencyValue',
  pure: false,
})
export class CurrencyValuePipe implements PipeTransform {
  constructor(private appService: AppService) {}

  result: string = '';
  value: any;
  currency: any;

  /**
   * 转换币种小数位数、占位符等功能
   * 在金额显示的时候，最多保留9位数，使用科学计数法显示(每3位之间为,号)
   * 数字长度大于9位数时，保留最大显示8位数，并以K,M(千，百万)来简写
   * 加密货币小数位后最多显示6位，法币小数位后最多显示2位，当数字超过7位时，自动省略小数位最后多余的位数
   * BTC/ETH小数位后最多显示8位，当数字超过9位时，自动省略小数位最后多余的位数 (已編輯)
   *
   * @param value 原始值
   * @param currency 币种
   * @param placeholder 非 undefined 时候，占位符，如果是字符串，则values是 0 和 空的时候 都是显示这个字符串，如果是字符串数组，则分别对应空时候显示 和 0时候显示
   * @param addPlus 如果是正数，前面是否添加 “+” 号
   * @param toFixed 非 undefined 时候，忽略数字小数最多8位，法币最多2位的限制，而使用此数字来限定小数位数。是数字数组的时候，则区分虚拟币[0]和法币[1]来处理
   * @returns
   */
  transform(
    value: any,
    currency: string,
    placeholder?: string | string[],
    addPlus?: boolean,
    toFixed?: number | (number | undefined)[],
  ): string {
    //有result且value和currency都没变(其余参数暂不考虑动态)，直接使用result
    if (this.value === value && this.currency === currency && this.result) return this.result;

    this.value = value;
    this.currency = currency;

    //placeholder判断，中途直接返回
    if (placeholder !== undefined && (value === '' || value === null || value === undefined)) {
      if (Array.isArray(placeholder)) {
        if (placeholder[0]) {
          this.result = placeholder[0];
          return this.result;
        }
      } else {
        this.result = placeholder;
        return this.result;
      }
    }
    if (placeholder !== undefined && value === 0) {
      if (Array.isArray(placeholder)) {
        if (placeholder[1]) {
          this.result = placeholder[1];
          return this.result;
        }
      } else {
        this.result = placeholder;
        return this.result;
      }
    }

    //防undefined、NaN等报错
    value = isNaN(Number(value)) ? 0 : Number(value);

    let prefix = '';
    const suffix = '';

    // 添加前缀
    if (addPlus && Number(value) > 0) {
      prefix = '+';
    } else if (Number(value) < 0) {
      // 负数去掉原始负号（方便下面计算位数），改为前缀拼接
      value = Math.abs(value);
      prefix = '-';
    }

    const currencies = this.appService.currencies$.value || [];
    const target = currencies.find((x: any) => x.currency === currency);
    if (!target) return prefix + value; // 找不到幣別配置時直接回傳原值（保持原行為）

    // === 規則判斷 ===
    const code = String(currency || '').toUpperCase();
    const isDigital: boolean = !!target.isDigital; // 沿用你的來源
    const isBTCETH: boolean = code === 'BTC' || code === 'ETH';

    // 預設小數限制（Rule 3 / Rule 4）
    // - 其他加密：最多 6 位
    // - 法幣：最多 2 位
    // - BTC/ETH：最多 8 位
    const defaultFrac = isBTCETH ? 8 : isDigital ? 6 : 2;

    // toFixed 覆寫（保留你原本 API），但不超過規則上限
    let fixed =
      toFixed !== undefined
        ? Array.isArray(toFixed)
          ? isDigital
            ? (toFixed[0] ?? defaultFrac)
            : (toFixed[1] ?? defaultFrac)
          : toFixed
        : defaultFrac;
    if (fixed == null || fixed < 0) fixed = 0;
    // 不突破規則上限
    fixed = Math.min(fixed, defaultFrac);

    // 取得整數位數（不含逗號與小數）
    const intDigits = value >= 1 ? Math.floor(Math.log10(value)) + 1 : 1;

    // === 規則 2：數字長度 > 9，使用 K/M 簡寫，保留最多 8 位有效數字 ===
    if (intDigits > 9) {
      // 需求只給 K/M，因此統一以 M（/1_000_000）簡寫（十億以上仍以 M 表示「多少百萬」）
      const base = value / 1_000_000;
      const compact = this.toSignificant(base, 8); // 最多 8 位有效數字
      this.result = prefix + compact + 'M';
      return this.result;
    }

    // === 規則 1 + 規則 3/4（<= 9 位時，千分位顯示、動態縮減小數） ===
    // 先按「幣種上限」進行小數截斷（保留你原先 toDecimal 風格）
    let v1 = Number(value).toDecimal(fixed);

    // 動態縮減小數位：
    // - 其他加密/法幣：當整數位 > 7 時，縮減小數位
    // - BTC/ETH：當整數位 > 9 時，縮減小數位（實際這支分支進不到，因為 >9 已在上面被 M 簡寫了；保留以符合規則）
    const limit = isBTCETH ? 9 : 7;
    if (intDigits > limit && fixed > 0) {
      const reduce = intDigits - limit;
      const newFixed = Math.max(0, fixed - reduce);
      v1 = Number(value).toDecimal(newFixed);
    }

    // 限制總位數最多 9 位（不含逗號），並加上逗號（保留你原有邏輯，但修正邊界）
    let [leftSide, rightSide = ''] = v1.split('.');
    const leftLen = leftSide.length;

    if (leftLen >= 9) {
      // 整數已達 9 位，僅加逗號，不再帶小數
      v1 = this.addComma(leftSide);
    } else {
      // 可顯示的小數長度 = min(9 - 整數位數, 目前小數長度)
      let maxDecimalByTotal = 9 - leftLen;
      if (maxDecimalByTotal < 0) maxDecimalByTotal = 0;
      // 同時也不能超過「幣種上限 fixed」
      const maxDecimal = Math.min(maxDecimalByTotal, fixed);
      rightSide = rightSide.slice(0, maxDecimal);
      v1 = this.addComma(leftSide) + (rightSide.length > 0 ? `.${rightSide}` : '');
    }

    this.result = prefix + v1 + suffix; // suffix 現階段只在簡寫時用到
    return this.result;
  }

  // === 工具：加逗號（保留你的方法） ===
  addComma(str: string) {
    return str.replace(/\d+/, function (n) {
      return n.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
    });
  }

  // === 工具：轉成最多 sig 位有效數字的字串（去科學記號尾零） ===
  private toSignificant(n: number, sig: number): string {
    let s = n.toPrecision(sig);
    if (/e/i.test(s)) {
      const num = Number(s);
      s = num.toFixed(Math.min(sig, 12));
    }
    // 去掉多餘 0 與小數點
    s = s
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '')
      .replace(/\.$/, '');
    return s;
  }
}
